import { Component, EventEmitter, Input, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Observable } from 'rxjs/Observable';
import { AnonymousSubscription } from 'rxjs/Subscription';
import { Marker, Geometry } from '@realpage-da/ng-map';
import { orderBy } from 'lodash';
import { PropertyDetailJSON, PropertyType, PROPERTY_TYPE, WalkScoreJSON } from '~core/model';
import { PropertyMarkerComponent } from '../map';
import { ColorConstants } from '~shared/utils/color-constants';
import { PropertyService, KeybindService, KEYBOARD_CODES, KeybindDescription, AnalyticsService } from '~core/services';
import { environment } from '~environment';
import { SaleInformationJSON, BuyerJSON, BrokerJSON, SellerJSON, LenderJSON } from '~/core/model/SaleInformation';
import { PropertyMetric } from '~/core/model/Property';
import { AppConstants } from '~/shared/utils/app-constants';

const REALPAGE_LOGO = `${environment.staticFiles}/images/realpage-logo-white.png`;
@Component({
  styleUrls: ['./property-detail-popup.component.scss'],
  templateUrl: './property-detail-popup.component.html'
})

export class PropertyDetailPopupComponent implements OnInit, OnDestroy {
  close = new EventEmitter();
  @Input('property') property: PropertyMetric;
  position: string;
  propertyType: PropertyType = this.position === 'S' ? PROPERTY_TYPE.SUBJECT : PROPERTY_TYPE.RENT_COMP;
  propertDetail: Observable<PropertyMetric>;
  markers: Observable<Marker[]>;
  walkScoreData: Observable<WalkScoreJSON>;
  showSalesInformation: boolean = false;
  saleInformation: SaleInformationJSON;
  buyerInformation: BuyerJSON;
  brokerInformation: BrokerJSON;
  sellerInformation: SellerJSON;
  lender: LenderJSON;
  properties: PropertyDetailJSON[] = [];
  subscriptions: AnonymousSubscription[] = [];
  underwriteNowUrl: string;
  isAssetSnapshotEnabled: boolean = false;

  rpLogo = REALPAGE_LOGO;

  constructor(private propertyService: PropertyService,
    private keybindService: KeybindService,
    public analytics: AnalyticsService,
    private location: Location) {
    this.subscriptions.push(this.keybindService.bind(new KeybindDescription(KEYBOARD_CODES.escape))
      .subscribe(x => this.onClickedExit()));
  }

  ngOnInit() {
    this.underwriteNowUrl = environment.underwriterUrl + '/#/' + this.property.propertyId + '/product-detail';

    this.propertDetail = this.propertyService
      .getPropertyCurtainDetail(this.property.propertyId, this.property.type === AppConstants.TYPES.STUDENT_HOUSING ? AppConstants.STUDENT_SHORT_TEXT : AppConstants.TYPES.CONVENTIONAL)
      .distinctUntilChanged()
      .map((prop: PropertyMetric) => {
        prop.saleInformation = orderBy(prop.saleInformation, ['salesDate'], ['desc']);
        if (prop.saleInformation && prop.saleInformation.length > 0) {
          this.saleInformation = prop.saleInformation[0];
          this.buyerInformation = prop.saleInformation[0].buyer;
          this.sellerInformation = prop.saleInformation[0].seller;
          this.brokerInformation = prop.saleInformation[0].broker;
          this.lender = prop.saleInformation[0].lender;
        }
        return prop;
      })
      .share();

    this.walkScoreData = this.propertDetail
      .filter(property => !!property)
      .flatMap(property => this.propertyService.getWalkScore(property.address, property.latitude, property.longitude))
      .share();

    this.markers = this.propertDetail
      .map(p => this.getMarkers(p))
      .share();
  }

  ngOnDestroy() {
    this.close.complete();
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }

  ngAfterViewInit() {
    let map = document.getElementsByClassName('map')[1] as HTMLElement;
    map.style.overflow = 'hidden';
    this.propertDetail
      .take(1)
      .subscribe(p => this.updateLocationState(`/main/p/${p.propertyUrlSlug}/${p.id}`));
  }

  onClickedExit() {
    this.close.emit('event');
  }

  getMarkers(property: PropertyMetric): Marker[] {
    return [{
      geometry: new Geometry.Point(+property.latitude || 32.76657, +property.longitude || -96.775466),
      id: property.propertyId,
      data: {
        id: property.propertyId,
        property: property,
        tooltip: {
          color: !property.position
            ? ColorConstants.GRAY_LIGHT
            : property.propertyType === PROPERTY_TYPE.SUBJECT.key
              ? ColorConstants.PURPLE
              : (property.propertyType === PROPERTY_TYPE.RENT_COMP.key
                ? ColorConstants.BLUE_RAUL
                : ColorConstants.ORANGE)
        },
      },
      options: {
        icon: {
          component: PropertyMarkerComponent
        }
      }
    }];
  }

  underwriteNow() {
    this.analytics.emitEvent('click', {
      event_category: 'property',
      event_label: 'underwrite-now',
      value: this.property.id
    });
  }

  updateLocationState(path: string): void {
    // Store query string for initial pageview logging
    const queryString = path === window.location.pathname
      ? window.location.search
      : '';

    this.location.go(path, queryString);

    this.analytics.logPageview(`${path}${queryString}`);
    this.analytics.emitEvent('load', {
      event_category: 'property',
      event_label: 'popup',
      value: this.property.id
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/features/property/components/popup/property-detail-popup.component.ts